import { Field } from '@/components/ui/field';
import { Input, Text } from '@chakra-ui/react';
import { euVatNumberShape, looksLikeEuVatNumber } from '@fluenceur/types';
import type { UseFormRegisterReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export interface BrandVatNumberFieldProps {
  /** ISO country code currently selected in the form, not the one last saved. */
  country: string;
  /** Current field value, read to decide whether the hint is worth showing. */
  value: string;
  registration: UseFormRegisterReturn;
}

/**
 * The intra-community VAT number, offered and never checked.
 *
 * The hint below the input compares the typed value against the shape the
 * selected member state publishes, and says nothing beyond that. A number that
 * carries the right shape has not been confirmed to exist, so no copy here may
 * read as a confirmation, and the hint never blocks a save.
 */
const HINT_ID = 'brand-vat-number-shape-hint';

const BrandVatNumberField = ({ country, value, registration }: BrandVatNumberFieldProps) => {
  const { t } = useTranslation();
  // The helper already answers "nothing to say" for an empty value, so the
  // emptiness is not re-tested here. One rule, in one place.
  const showShapeHint = !looksLikeEuVatNumber(country, value);

  return (
    <Field label={t('settings.vatNumber')} helperText={t('settings.vatNumberHelp')}>
      {/* The length bound matches the server's, so the one refusal this field
          can still produce is unreachable by typing. The longest real number in
          the union is fourteen characters, so sixty-four never stops anyone. */}
      <Input
        {...registration}
        variant="outline"
        maxLength={64}
        placeholder={euVatNumberShape(country)?.example}
        aria-describedby={showShapeHint ? HINT_ID : undefined}
      />
      {showShapeHint && (
        <Text id={HINT_ID} fontSize="xs" color="fg.muted" mt={1}>
          {t('settings.brandInformationForm.vatNumberShapeHint')}
        </Text>
      )}
    </Field>
  );
};

export default BrandVatNumberField;
